import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getItineraryLegacySyncStatus } from "../repo/itineraryRepo";

const warnings = {
  blocked: "新系统里已保存的行程还不能更新到旧系统，因为两边的线路没有对应好。请联系管理员处理。",
  retrying: "旧系统还没有更新成功，系统会自动再试。已保存的内容仍在新系统，稍后可点“重新检查同步”查看结果。",
  failed: "系统已经重试了几次，旧系统仍没有更新成功。已保存的内容仍在新系统，请联系管理员处理。",
  not_scheduled: "新系统里已有保存记录，但查不到更新旧系统的进度。请联系管理员检查。",
  superseded: "旧系统还没有更新到新系统中最近保存的内容。请稍后点“重新检查同步”查看结果。",
};

/** 保存成功后有界跟踪旧库任务；警告不改写编辑器保存状态。 */
export function useItineraryLegacySyncWarning(itineraryId: string, missingAssociation = false) {
  const sessionId = useId();
  const [check, setCheck] = useState<{ itineraryId: string; startedAt: number; sequence: number }>();
  const sequenceRef = useRef(0);
  useEffect(() => setCheck(undefined), [itineraryId]);
  const active = check?.itineraryId === itineraryId;
  const query = useQuery({
    queryKey: ["itinerary", "legacy-sync-status", itineraryId, sessionId, check?.sequence],
    enabled: active,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => getItineraryLegacySyncStatus(itineraryId),
    refetchInterval: (current) => active && !current.state.error
      && ["pending", "running"].includes(current.state.data?.status ?? "")
      && Date.now() - check.startedAt < 60_000 ? 2_000 : false,
  });
  const checkAfterSave = () => setCheck({
    itineraryId, startedAt: Date.now(), sequence: ++sequenceRef.current,
  });
  const status = query.data?.status;
  const waitingExpired = active && ["pending", "running"].includes(status ?? "")
    && Date.now() - check.startedAt >= 60_000;
  const warning = !active ? undefined : query.isError
    ? "暂时查不到旧系统是否已更新，已保存的内容仍在新系统。请稍后点“重新检查同步”再看结果。"
    : waitingExpired
      ? "旧系统还在等待更新或正在更新，已保存的内容仍在新系统。请稍后点“重新检查同步”查看结果。"
      : status === "blocked" && missingAssociation
        ? "行程已保存在新系统，但还没有选好对应的定制需求或商机，旧系统暂时无法更新。请先补齐这些信息，再保存一次。"
        : status && status in warnings ? warnings[status as keyof typeof warnings] : undefined;
  return { warning: warning ? `${warning}这条提示只说明旧系统的更新情况；本次修改是否保存成功，请看页面顶部的保存状态。` : undefined, checking: active && query.isFetching, checkAfterSave };
}
